import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {Droppable, Draggable} from 'react-beautiful-dnd';

import {Input, WhiteBox, IconButton, DragButton} from 'components';
import {OPERATE_TYPES} from 'api/constants';
import {useError} from 'hooks/useError';

import {updateBitTitle, deleteBit, updateBitDuration} from './redux/actions';

import {Bit, Component} from './types';
import {INTERACTIVE, BIT_FIELDS, FIELDS} from './constants';
import {saveWisbit} from './redux/thunks';

import BitComponent from './BitComponent';
import AddComponentModal from './AddComponentModal';
import NewComponentButton from './NewComponentButton';
import i18n from 'i18next';

type DraggableBitProps = {
  index: number;
  bitData: Bit;
  disabled?: boolean;
};

function DraggableBit({index, bitData, disabled}: DraggableBitProps) {
  // Whether to show new component modal or not.
  const [showAddComponentModal, setShowAddComponentModal] = useState(false);

  // Component that was selected for editing.
  const [selectedComponent, setSelectedComponent] = useState<Component>();

  // Ugh
  const {setValidationError, errorMessage} = useError();

  // Redux
  const dispatch = useDispatch();

  // Event handlers

  const onShowEditComponent = (selectedId: string) => {
    const selected = bitData.components?.find(({id}) => id === selectedId);

    setSelectedComponent(selected);
    setShowAddComponentModal(true);
  };

  const onDeleteBit = () => {
    dispatch(deleteBit(bitData.id));

    dispatch(
      saveWisbit({
        op: OPERATE_TYPES.REMOVE,
        field: FIELDS.BITS,
        to: {bitIndex: index},
      })
    );
  };

  const onSave = (bitField: BIT_FIELDS) => () => {
    setValidationError(undefined);

    if (bitField === BIT_FIELDS.TITLE && !bitData.title) {
      setValidationError({
        field: BIT_FIELDS.TITLE,
        message: i18n.t('wisbitPage.draggableBit.validationErrors.title'),
      });
    } else if (
      bitField === BIT_FIELDS.DURATION &&
      (!bitData.duration || bitData.duration <= 0)
    ) {
      setValidationError({
        field: BIT_FIELDS.DURATION,
        message: i18n.t('wisbitPage.draggableBit.validationErrors.duration'),
      });
    } else {
      dispatch(
        saveWisbit({
          op: OPERATE_TYPES.REPLACE,
          field: FIELDS.BITS,
          bitField,
          to: {
            bitIndex: index,
          },
        })
      );
    }
  };

  const handleAddComponentModalClose = () => {
    setShowAddComponentModal(false);
    setSelectedComponent(undefined);
  };

  const disabledInteractive = bitData.components
    ? bitData.components.findIndex(
        ({type}) => type && INTERACTIVE.includes(type)
      ) > -1
    : false;

  return (
    <Draggable draggableId={bitData.id} index={index}>
      {(provided, snapshot) => (
        <div
          className="w-1/2 min-h-44 odd:pr-3 even:pl-3 mb-6 relative"
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <WhiteBox className="p-4" showShadow={snapshot.isDragging}>
            <div className={`flex justify-end ${disabled ? 'invisible' : ''}`}>
              <IconButton type="delete" onClick={onDeleteBit} />
              <DragButton {...provided.dragHandleProps} />
            </div>
            <Input
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(updateBitTitle(bitData.id, event.target.value));
              }}
              onBlur={onSave(BIT_FIELDS.TITLE)}
              value={bitData.title}
              placeholder={i18n.t('wisbitPage.draggableBit.addTitle')}
              className="placeholder-dodger-blue"
              disabled={disabled}
              errorMessage={errorMessage && errorMessage(BIT_FIELDS.TITLE)}
            />
            <Droppable droppableId={bitData.id} type="component">
              {provided => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {bitData.components &&
                    bitData.components.map((component, index) => (
                      <BitComponent
                        key={component.id}
                        data={component}
                        index={index}
                        onEdit={onShowEditComponent}
                        disabled={disabled}
                      />
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
            <div className="flex justify-btween items-center mt-3 relative">
              <div className="w-1/2 flex justify-between items-center">
                <div className="w-1/2">
                  <Input
                    type="number"
                    min="0"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      dispatch(
                        updateBitDuration(
                          bitData.id,
                          Number(event.target.value)
                        )
                      );
                    }}
                    onBlur={onSave(BIT_FIELDS.DURATION)}
                    value={bitData.duration}
                    placeholder={i18n.t(
                      'wisbitPage.draggableBit.addLearningTime'
                    )}
                    className="my-2 placeholder-dodger-blue"
                    disabled={disabled}
                    errorMessage={
                      errorMessage && errorMessage(BIT_FIELDS.DURATION)
                    }
                  />
                </div>
                <div className="w-1/2 p-2">
                  <span>min(s)</span>
                </div>
              </div>
              {!disabled && (
                <NewComponentButton
                  onClick={() => setShowAddComponentModal(true)}
                  disabled={
                    bitData.components && bitData.components.length >= 5
                  }
                />
              )}
            </div>
          </WhiteBox>
          <AddComponentModal
            visible={showAddComponentModal}
            bitIndex={index}
            bitData={bitData}
            component={selectedComponent}
            disabled={disabled}
            disabledInteractive={disabledInteractive}
            onClose={handleAddComponentModalClose}
          />
        </div>
      )}
    </Draggable>
  );
}

export default DraggableBit;
